import { getProfitAndLoss } from "../../apiClient";
import { formatDateForAPI } from "../dateUtils";
import { groupByCategory, groupBySource, groupByType, sumByCategory } from "../collectionUtils";
import { pickDimensionFilters } from "../dimensionParams";
import { formatReportApiError, isRequestCancelled } from "../errorUtils";
import { getMockReportData } from "../mockData";

export const formatVerticalIncomeData = (data) => {
  const revenue = sumByCategory(data.revenue || []);
  const cogs = sumByCategory(data.cost_of_goods_sold || []);
  const expenses = sumByCategory(data.expenses || []);
  const otherIncome = sumByCategory(data.other_income || []);
  const otherExpenses = sumByCategory(data.other_expenses || []);
  const grossProfit = revenue - cogs;
  const operatingProfit = grossProfit - expenses;
  const netIncome = operatingProfit + otherIncome - otherExpenses;

  return {
    summary: {
      revenue,
      cogs,
      grossProfit,
      expenses,
      operatingProfit,
      otherIncome,
      otherExpenses,
      netIncome,
    },
    details: {
      revenue: groupByCategory(data.revenue || []),
      cogs: groupByCategory(data.cost_of_goods_sold || []),
      expenses: groupByCategory(data.expenses || []),
      otherIncome: groupByCategory(data.other_income || []),
      otherExpenses: groupByCategory(data.other_expenses || []),
    },
    ratios: {
      grossMargin: revenue ? (grossProfit / revenue) * 100 : 0,
      operatingMargin: revenue ? (operatingProfit / revenue) * 100 : 0,
      netMargin: revenue ? (netIncome / revenue) * 100 : 0,
      expenseRatio: revenue ? (expenses / revenue) * 100 : 0,
    },
  };
};

const formatEfficiencyData = (data, filters) => {
  const revenue = sumByCategory(data.revenue || []);
  const expenses = sumByCategory(data.expenses || []);
  const netIncome = revenue - expenses;

  return {
    summary: { revenue, expenses, netIncome },
    ratios: {
      expenseToRevenue: revenue ? (expenses / revenue) * 100 : 0,
      profitability: revenue ? (netIncome / revenue) * 100 : 0,
      expenseCategories: (data.expenses || []).map((expense) => ({
        category: expense.category,
        amount: expense.amount,
        percentage: revenue ? (expense.amount / revenue) * 100 : 0,
      })),
    },
    benchmarks: {
      targetExpenseRatio: filters.target_expense_ratio || 60,
      targetProfitMargin: filters.target_profit_margin || 15,
    },
  };
};

const formatPnlData = (data, reportType, filters) => {
  if (reportType === "efficiency") return formatEfficiencyData(data, filters);
  if (reportType === "expense") {
    const expenses = data.expenses || [];
    return {
      totalExpenses: sumByCategory(expenses),
      byCategory: groupByCategory(expenses),
      byType: groupByType(expenses),
      largestExpenses: [...expenses].sort((a, b) => b.amount - a.amount).slice(0, 10),
    };
  }
  if (reportType === "revenue") {
    const revenue = data.revenue || [];
    return {
      totalRevenue: sumByCategory(revenue),
      byCategory: groupByCategory(revenue),
      bySource: groupBySource(revenue),
      topRevenueSources: [...revenue].sort((a, b) => b.amount - a.amount).slice(0, 10),
    };
  }
  return formatVerticalIncomeData(data);
};

export const handleVerticalIncome = async (filters, periodDate, firstDayOfMonth, reportType, language = "ar", options = {}) => {
  try {
    const params = {
      currency: filters.currency,
      date_after: formatDateForAPI(firstDayOfMonth),
      date_before: formatDateForAPI(periodDate),
      compare_period_unit: filters.compare_period_unit || "month",
      ...(filters.category && { category: filters.category }),
      ...(filters.account_type && { account_type: filters.account_type }),
      ...pickDimensionFilters(filters),
    };

    if (reportType === "expense") params.transaction_type = "expense";
    if (reportType === "revenue") params.transaction_type = "revenue";
    if (reportType === "efficiency" && filters.efficiency_metric) params.metric = filters.efficiency_metric;

    const apiResponse = await getProfitAndLoss(params, { signal: options.signal });

    return {
      success: true,
      reportType,
      isRealData: true,
      originalData: apiResponse.data,
      data: formatPnlData(apiResponse.data, reportType, filters),
    };
  } catch (error) {
    if (isRequestCancelled(error)) throw error;
    console.error("P&L API Error:", error);
    return {
      success: false,
      reportType,
      isRealData: false,
      data: getMockReportData(reportType, filters),
      error: formatReportApiError(error, language),
    };
  }
};
